/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { IconButton } from "@mui/material";
import { ArrowUpward, ArrowDownward } from "@mui/icons-material";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useSelector, useDispatch } from "react-redux";

import ApiCalls from "services/flick_erp_api_call";
import { useEffect, useMemo, useState } from "react";
import { baseEmployeeList } from "./employeesSlice";
import Avatar from "@mui/material/Avatar";
import {
  CalendarMonth,
  EmailTwoTone,
  Groups2,
  PersonAddAlt1,
  PhoneAndroidTwoTone,
} from "@mui/icons-material";
import MDButton from "components/MDButton";
import { useNavigate } from "react-router-dom";
import { Autocomplete, Box, Chip, Divider, Grid, Stack, TextField, Typography } from "@mui/material";
import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";
import { useTheme } from "@mui/material/styles";
import { useMaterialUIController } from "context";

function Employees({ permissions, modulePermissions }) {
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const palette = theme.palette;
  const [status, setStatus] = useState({ val: "active", name: "Active" });
  const [employeesData, setEmployeesData] = useState({
    columns: [
      { Header: "Emp ID", accessor: "emp_id", width: "10%" },
      {
        Header: "Name",
        accessor: "firstname",
        Cell: ({ row }) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Avatar alt="Profile Photo">
              {row.original.profilePhoto.length > 0 ? (
                <img
                  src={row.original.profilePhoto}
                  style={{
                    width: "50px",
                    height: "50px",
                    objectFit: "cover",
                    borderRadius: "50%",
                  }}
                />
              ) : (
                row.original.firstname.charAt(0).toUpperCase()
              )}
            </Avatar>
            <span style={{ marginLeft: "0.5rem" }}>
              {row.original.firstname}
            </span>
            <span style={{ marginLeft: "0.2rem" }}>
              {row.original.lastname}
            </span>
          </div>
        ),
      },
      { Header: "Designation", accessor: "designation" },

      { Header: "email", accessor: "email" },
      { Header: "manager", accessor: "manager_name" },
      { Header: "Date of joining", accessor: "doj" },
    ],
    rows: [],
  });

  const dispatch = useDispatch();
  const storedBaseEmployees = useSelector((state) => state.baseEmployees.baseEmployee || []);

  useEffect(() => {
  if (status.val === "active" && storedBaseEmployees.length) {
    setEmployeesData(prev => ({ ...prev, rows: storedBaseEmployees }));
  } else{
    getBaseEmployees(`?status=${status.val}`);
 }
}, [status]);

  async function getBaseEmployees(queryParams) {
    try {
      const res = queryParams === `?status=active` ? await ApiCalls.getBaseEmployeesList(queryParams) : await ApiCalls.getEmployeesList(queryParams);
       
      if (res?.success && res.code === 200) {
        const baseData = JSON.parse(JSON.stringify(res.data || []));

        baseData.forEach((o) => {
          if (o && o.user && o.id) {
            o.user.id = o.id;
          }
        });

        const baseRows = baseData.map((o) => ({
          ...(o.user || {}),
          dob: o?.dob,
          emp_id: o?.emp_id,
          designation: o?.designation?.name || "",
          doj: o?.doj,
          profilePhoto: o?.img || "",
          manager_name: o?.manager_name,
          status: o?.status,
        }));

        setEmployeesData((prev) => ({ ...prev, rows: baseRows }));
        dispatch(baseEmployeeList(baseRows));
      }
    } catch (error) {
      console.log("Error dispatching base employees:", error);
    }
  }

  const navigate = useNavigate();

  const handleAddClick = () => {
    navigate("/pages/profile/profile-overview/", {
      state: {
        isCreateEmployee: true,
        permissions: permissions,
      },
    });
  };

  const handleCardClick = (employee) => {
    navigate("/employees/employee_view/", {
      state: {
        userid: employee.id,
        permissions: permissions,
      },
    });
  }


  const [sortOrder, setSortOrder] = useState("asc");
  const [employees, setEmployees] = useState([]);

  const [filteredEmployees, setFilteredEmployees] = useState(employeesData.rows || []);

  const sortedEmployees = useMemo(() => {
    return [...filteredEmployees].sort((a, b) =>
      sortOrder === "asc" ? a.emp_id - b.emp_id : b.emp_id - a.emp_id
    );
  }, [filteredEmployees, sortOrder]);

  useEffect(() => {
    setEmployees(employeesData.rows); // Initialize employees state
  }, [employeesData]);

  // Sorting function
  const handleSort = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState({ val: "active", name: "Active" });

  useEffect(() => {
    let filtered = employeesData.rows;

    if (statusFilter.val !== "all") {
      filtered = filtered.filter((emp) => emp.status === statusFilter.val);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((emp) => {
        const fullName = `${emp.firstname} ${emp.lastname}`.toLowerCase();
        return (
          emp.firstname?.toLowerCase().includes(query) ||
          emp.lastname?.toLowerCase().includes(query) ||
          fullName.includes(query) ||
          String(emp.emp_id || "").includes(query) ||
          emp.email?.toLowerCase().includes(query) ||
          emp.designation?.toLowerCase().includes(query)
        );
      });
    }

    setFilteredEmployees(filtered);
  }, [searchQuery, statusFilter, employeesData]);

  const formatDate = (dateString) => {
    if (!dateString) return "--";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "--";
    return date.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
  };

  const metrics = useMemo(() => {
    const total = employeesData.rows.length;
    const active = employeesData.rows.filter((employee) => employee.status === "active").length;
    const notice = employeesData.rows.filter((employee) => employee.status === "notice").length;

    return [
      {
        label: "Total employees",
        value: total,
        helper: "Visible across the directory",
      },
      {
        label: "Active",
        value: active,
        helper: "Currently working",
      },
      {
        label: "On notice",
        value: notice,
        helper: "Needs follow-up",
      },
    ];
  }, [employeesData.rows]);

  const getStatusChipColor = (employeeStatus) => {
    if (employeeStatus === "active") return "success";
    if (employeeStatus === "notice") return "warning";
    if (employeeStatus === "deactive") return "error";
    return "default";
  };

  const ui = {
    pageHero: darkMode
      ? `linear-gradient(180deg, ${rgba(palette.background.default, 0.98)} 0%, ${rgba(
          palette.background.card,
          0.94
        )} 100%)`
      : `linear-gradient(180deg, ${rgba(colors.light.main, 0.94)} 0%, ${rgba(
          colors.white.main,
          0.98
        )} 100%)`,
    sectionBg: darkMode
      ? `linear-gradient(180deg, ${palette.background.card} 0%, ${palette.background.muted} 100%)`
      : `linear-gradient(180deg, ${rgba(colors.light.main, 0.94)} 0%, ${rgba(colors.white.main, 0.98)} 100%)`,
    card: darkMode ? palette.background.card : colors.white.main,
    soft: darkMode ? palette.background.muted : colors.light.main,
    softPanel: darkMode
      ? `linear-gradient(180deg, ${palette.background.muted} 0%, ${rgba(palette.background.card, 0.98)} 100%)`
      : `linear-gradient(180deg, ${colors.light.main} 0%, ${colors.background.accent} 100%)`,
    border: darkMode ? rgba(palette.white.main, 0.08) : rgba(colors.secondary.main, 0.14),
    title: darkMode ? palette.text.focus : colors.dark.main,
    body: darkMode ? palette.text.main : colors.secondary.main,
    muted: darkMode ? rgba(palette.white.main, 0.62) : colors.text.main,
    empty: darkMode
      ? `linear-gradient(180deg, ${palette.background.card} 0%, ${palette.background.muted} 100%)`
      : `linear-gradient(180deg, ${colors.white.main} 0%, ${colors.light.main} 100%)`,
    hoverShadow: darkMode ? "0 20px 45px rgba(2, 6, 23, 0.38)" : `0 20px 45px ${rgba(colors.dark.main, 0.12)}`,
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={4} pb={3}>
        <MDBox
          mb={3}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: "28px",
            background:
              `linear-gradient(135deg, ${rgba(colors.primary.main, 0.98)} 0%, ${rgba(colors.info.focus, 0.95)} 55%, ${rgba(colors.info.main, 0.88)} 100%)`,
            color: colors.white.main,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <MDBox
            sx={{
              position: "absolute",
              inset: 0,
              background:
                `radial-gradient(circle at top right, ${rgba(colors.white.main, 0.18)}, transparent 28%), radial-gradient(circle at bottom left, ${rgba(colors.white.main, 0.1)}, transparent 30%)`,
              pointerEvents: "none",
            }}
          />
          <Grid container spacing={3} position="relative">
            <Grid item xs={12} lg={7}>
              <MDTypography variant="overline" sx={{ color: rgba(colors.white.main, 0.72), letterSpacing: "0.12em" }}>
                Workforce directory
              </MDTypography>
              <MDTypography variant="h3" color="white" mt={1} mb={1.5}>
                Employee hub built for faster people operations
              </MDTypography>
              <MDTypography variant="body2" color="white" sx={{ opacity: 0.82, maxWidth: 620 }}>
                Review your workforce, search quickly, and move into employee records from one polished
                directory experience.
              </MDTypography>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Grid container spacing={2}>
                {metrics.map((metric) => (
                  <Grid item xs={12} sm={4} lg={12} xl={4} key={metric.label}>
                    <Card
                      sx={{
                        p: 2.25,
                        height: "100%",
                        backgroundColor: rgba(colors.white.main, 0.12),
                        border: `1px solid ${rgba(colors.white.main, 0.14)}`,
                        backdropFilter: "blur(12px)",
                        color: "white",
                      }}
                    >
                      <MDTypography variant="caption" sx={{ color: rgba(colors.white.main, 0.72) }}>
                        {metric.label}
                      </MDTypography>
                      <MDTypography variant="h4" color="white" mt={0.5}>
                        {metric.value}
                      </MDTypography>
                      <MDTypography variant="body2" sx={{ color: rgba(colors.white.main, 0.76) }}>
                        {metric.helper}
                      </MDTypography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </MDBox>

        <Card sx={{ overflow: "hidden" }}>
          <MDBox
            p={{ xs: 2.5, md: 3.5 }}
            sx={{
              borderBottom: `1px solid ${ui.border}`,
              background: ui.sectionBg,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} lg={4}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <MDBox
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "16px",
                      display: "grid",
                      placeItems: "center",
                      background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.info.main} 100%)`,
                      color: colors.white.main,
                    }}
                  >
                    <Groups2 />
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="h5" sx={{ color: ui.title }}>Employees</MDTypography>
                    <MDTypography variant="body2" sx={{ color: ui.body }}>
                      {sortedEmployees.length} employee records available
                    </MDTypography>
                  </MDBox>
                </Stack>
              </Grid>

              <Grid item xs={12} lg={8}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  justifyContent="flex-end"
                  alignItems={{ xs: "stretch", md: "center" }}
                >
                  <TextField
                    label="Search employees"
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ minWidth: { xs: "100%", md: 280 } }}
                  />
                  {permissions?.includes(modulePermissions?.update_permission) && (
                    <Autocomplete
                      disableClearable
                      value={statusFilter}
                      options={[
                        { val: "active", name: "Active" },
                        { val: "deactive", name: "Inactive" },
                        { val: "notish", name: "On Notice" },
                      ]}
                      getOptionLabel={(option) => option.name}
                      onChange={(event, newValue) => {
                        setStatus(newValue);
                        setStatusFilter(newValue);
                      }}
                      sx={{ minWidth: { xs: "100%", md: 180 } }}
                      renderInput={(params) => (
                        <TextField {...params} label="Status" variant="outlined" size="small" />
                      )}
                    />
                  )}
                  <IconButton
                    onClick={handleSort}
                    sx={{
                      width: 42,
                      height: 42,
                      border: `1px solid ${ui.border}`,
                      borderRadius: "12px",
                      color: ui.title,
                    }}
                  >
                    {sortOrder === "asc" ? <ArrowUpward /> : <ArrowDownward />}
                  </IconButton>
                  {permissions?.includes(modulePermissions?.create_permission) && (
                    <MDButton
                      //variant="gradient"
                      color="info"
                      startIcon={<PersonAddAlt1 />}
                      onClick={handleAddClick}
                    >
                      Add employee
                    </MDButton>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </MDBox>
					 {/* <DataTable
              table={employeesData}
              isEmployeeTable={true}
              canSearch
              permissions={permissions}
              sort={permissions.includes(modulePermissions.update_permission) &&(<Autocomplete
                value={status}
                options={[{val:"active", name:"Active"},{val:"deactive",name:"Deactive"},{val:"notish", name:"In Notice"}]}
                getOptionLabel={(e)=> e?.name}
                onChange={(event, newValue) => {
                  console.log("NEW VALUE", newValue)
                  setStatus(newValue ?? {val:"active", name:"Active"});
                }}
             
                sx={{ width: "240px",paddingLeft:"5px", paddingRight: "5px" }}
                renderInput={(params) => (
                  <TextField {...params} variant="standard"   InputProps={{...params.InputProps, disableUnderline: true}}
                  />
                )}
              />)}
            ></DataTable> */}

          <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              {sortedEmployees?.map((employee) => (
                <Grid item xs={12} sm={6} xl={4} key={employee.id}>
                  <Card
                    onClick={() => handleCardClick(employee)}
                    sx={{
                      p: 2.5,
                      height: "100%",
                      backgroundColor: ui.card,
                      border: `1px solid ${ui.border}`,
                      cursor: "pointer",
                      transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: ui.hoverShadow,
                        borderColor: rgba(colors.info.main, 0.22),
                      },
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2.5}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          src={employee?.profilePhoto}
                          alt={employee?.firstname}
                          sx={{
                            width: 64,
                            height: 64,
                            bgcolor: "info.main",
                            fontWeight: 800,
                            fontSize: 24,
                          }}
                        >
                          {!employee?.profilePhoto && employee?.firstname?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <MDBox>
                          <Typography variant="h6" sx={{ color: ui.title }}>
                            {employee?.firstname} {employee?.lastname}
                          </Typography>
                          <Typography variant="body2" sx={{ color: ui.body }}>
                            {employee?.designation || "No designation"}
                          </Typography>
                          <Typography variant="caption" sx={{ color: ui.muted }}>
                            ID: GE-{employee?.emp_id || "--"}
                          </Typography>
                        </MDBox>
                      </Stack>
                      <Chip
                        label={employee?.status || "Unknown"}
                        color={getStatusChipColor(employee?.status)}
                        size="small"
												la
                        sx={{ textTransform: "capitalize", color:"White"  }}
                      />
                    </Stack>

                    <MDBox
                      sx={{
                        p: 2,
                        borderRadius: "18px",
                        background: ui.softPanel,
                        border: `1px solid ${ui.border}`,
                      }}
                    >
                      <Stack spacing={1.25}>
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <EmailTwoTone sx={{ color: colors.info.main, fontSize: 20 }} />
                          <Typography variant="body2" sx={{ color: ui.body }} noWrap>
                            {employee.email || "--"}
                          </Typography>
                        </Stack>
                        {/*<Stack direction="row" spacing={1.2} alignItems="center">
                          <PhoneAndroidTwoTone sx={{ color: colors.info.main, fontSize: 20 }} />
                          <Typography variant="body2" sx={{ color: ui.body }}>
                            {employee.phone || "--"}
                          </Typography>
                        </Stack>*/}
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <Groups2 sx={{ color: colors.info.main, fontSize: 20 }} />
                          <Typography variant="body2" sx={{ color: ui.body }}>
                            Manager: {employee.manager_name || "--"}
                          </Typography>
                        </Stack>
                      </Stack>
                    </MDBox>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <MDBox
                          sx={{
                            p: 1.5,
                            borderRadius: "16px",
                            backgroundColor: ui.soft,
                            border: `1px solid ${ui.border}`,
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                            <CalendarMonth sx={{ color: colors.info.main, fontSize: 18 }} />
                            <Typography variant="caption" sx={{ color: ui.muted }}>
                              Birth date
                            </Typography>
                          </Stack>
                          <Typography variant="body2" sx={{ color: ui.title, fontWeight: 700 }}>
                            {formatDate(employee?.dob)}
                          </Typography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6}>
                        <MDBox
                          sx={{
                            p: 1.5,
                            borderRadius: "16px",
                            backgroundColor: ui.soft,
                            border: `1px solid ${ui.border}`,
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                            <CalendarMonth sx={{ color: colors.info.main, fontSize: 18 }} />
                            <Typography variant="caption" sx={{ color: ui.muted }}>
                              Joining date
                            </Typography>
                          </Stack>
                          <Typography variant="body2" sx={{ color: ui.title, fontWeight: 700 }}>
                            {formatDate(employee?.doj)}
                          </Typography>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {!sortedEmployees.length && (
              <Card
                sx={{
                  mt: 2,
                  p: 4,
                  textAlign: "center",
                  background: ui.empty,
                  border: `1px solid ${ui.border}`,
                }}
              >
                <MDTypography variant="h5" mb={1} sx={{ color: ui.title }}>
                  No employees found
                </MDTypography>
                <MDTypography variant="body2" sx={{ color: ui.body }}>
                  Try changing the search text or status filter to widen the results.
                </MDTypography>
              </Card>
            )}
          </Box>
        </Card>
      </MDBox>
      {employees.length > 0 && <Footer />}
    </DashboardLayout>
  );
}

export default Employees;
